import { defineProperty as _defineProperty } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React__default from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { IlOverflowMenu } from '../../../IlOverflowMenu/IlOverflowMenu/index.js';
import 'carbon-components-react';
import { isEnterKeyPressed, isSpaceKeyPressed } from '../../../../shared/utils/keysUtils.js';
import { cssClassPrefix } from '../../../../shared/constants/generalConstants.js';

var BASE_CSS = "".concat(cssClassPrefix, "-header-action");

/**
 * IlHeaderAction Component
 * #### Simple Action with Text, Icon or both
 * ```javascript
 * <IlHeaderAction title="Text" onClick={() => void}/>
 * <IlHeaderAction icon={ReactElement} onClick={() => void}/>
 * <IlHeaderAction title="Text" icon={ReactElement} onClick={() => void}/>
 * ```
 * #### Action with dropdown options
 * ```javascript
 * <IlHeaderAction title="" onClick={() => void} icon={ReactElement} caretDown={ReactElement} userProfile={fullName: "", email: ""}>
 *   <IlHeaderActionItem itemText="" hasDivider={boolean} onClick={() => void} active={boolean} closeMenuOnClick={boolean}>
 * </IlHeaderAction>
 * ```
 */
var IlHeaderAction = function IlHeaderAction(props) {
  var _props$isMobile = props.isMobile,
    isMobile = _props$isMobile === void 0 ? false : _props$isMobile,
    _props$icon = props.icon,
    icon = _props$icon === void 0 ? null : _props$icon,
    onClick = props.onClick,
    title = props.title,
    mobileTitle = props.mobileTitle,
    userProfile = props.userProfile,
    children = props.children,
    caretDown = props.caretDown,
    _props$deskTopMenuBut = props.deskTopMenuButtonAriaLabel,
    deskTopMenuButtonAriaLabel = _props$deskTopMenuBut === void 0 ? 'Actions' : _props$deskTopMenuBut,
    selectorPrimaryFocus = props.selectorPrimaryFocus;
  var hasDropdown = !!children;

  // Accessibility
  var onKeyPressHandler = function onKeyPressHandler(e) {
    if (isEnterKeyPressed(e) || isSpaceKeyPressed(e)) {
      onClick(e);
    }
  };
  var renderDropdownIcon = function renderDropdownIcon() {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, title && /*#__PURE__*/React__default.createElement("span", {
      className: classNames("".concat(BASE_CSS, "__title"), _defineProperty({}, "".concat(BASE_CSS, "--title-with-icon"), title && icon))
    }, title), icon, caretDown && /*#__PURE__*/React__default.createElement("span", {
      className: "".concat(BASE_CSS, "__caret-down")
    }, caretDown));
  };
  var renderProfileMobile = function renderProfileMobile() {
    return /*#__PURE__*/React__default.createElement("li", {
      className: "".concat(BASE_CSS, "__mobile-profile"),
      role: "none"
    }, icon && /*#__PURE__*/React__default.createElement("span", {
      className: "".concat(BASE_CSS, "__profile-icon")
    }, icon), /*#__PURE__*/React__default.createElement("div", {
      className: "".concat(BASE_CSS, "__profile-wrapper")
    }, /*#__PURE__*/React__default.createElement("span", {
      className: "".concat(BASE_CSS, "__profile-name")
    }, userProfile.fullName), /*#__PURE__*/React__default.createElement("span", {
      className: "".concat(BASE_CSS, "__profile-email")
    }, userProfile.email)));
  };
  var renderSimpleAction = function renderSimpleAction() {
    if (!title && !icon) {
      return null;
    }

    // @TODO Refactor focus within SO-114
    return /*#__PURE__*/React__default.createElement("div", {
      className: classNames(BASE_CSS, "".concat(BASE_CSS, "--simple-action"), _defineProperty({}, "".concat(BASE_CSS, "--clickable"), onClick), _defineProperty({}, "".concat(BASE_CSS, "--is-profile"), userProfile)),
      onClick: onClick || function () {},
      tabIndex: 0,
      role: "button",
      onKeyPress: onKeyPressHandler
    }, title && /*#__PURE__*/React__default.createElement("span", {
      className: classNames("".concat(BASE_CSS, "__title"), _defineProperty({}, "".concat(BASE_CSS, "--title-with-icon"), title && icon))
    }, title), icon);
  };
  var renderDropdownAction = function renderDropdownAction() {
    return /*#__PURE__*/React__default.createElement("div", {
      className: classNames(BASE_CSS, _defineProperty({}, "".concat(BASE_CSS, "--is-profile"), userProfile))
    }, /*#__PURE__*/React__default.createElement("div", {
      className: "".concat(BASE_CSS, "__desktop"),
      "data-floating-menu-container": true
    }, /*#__PURE__*/React__default.createElement(IlOverflowMenu, {
      className: classNames(BASE_CSS, "".concat(BASE_CSS, "--dropdown-action"), _defineProperty({}, "".concat(cssClassPrefix, "-header--clickable"), onClick), _defineProperty({}, "".concat(BASE_CSS, "--with-text"), title)),
      flipped: true,
      ariaLabel: deskTopMenuButtonAriaLabel,
      renderIcon: renderDropdownIcon,
      onClick: onClick || function () {},
      key: "".concat(title),
      selectorPrimaryFocus: selectorPrimaryFocus
    }, children)));
  };
  var renderMobileDropdownAction = function renderMobileDropdownAction() {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, userProfile && renderProfileMobile(), mobileTitle && /*#__PURE__*/React__default.createElement("li", {
      role: "none",
      className: "".concat(BASE_CSS, "__mobile-title")
    }, mobileTitle), React__default.Children.map(children, function (child) {
      return /*#__PURE__*/React__default.cloneElement(child, {
        isMobile: true
      });
    }));
  };
  var renderAction = function renderAction() {
    if (hasDropdown) {
      if (isMobile) {
        return renderMobileDropdownAction();
      }
      return renderDropdownAction();
    }
    return renderSimpleAction();
  };
  return renderAction();
};
IlHeaderAction.propTypes = {
  isMobile: PropTypes.bool,
  icon: PropTypes.node,
  caretDown: PropTypes.node,
  deskTopMenuButtonAriaLabel: PropTypes.string.isRequired,
  mobileTitle: PropTypes.string,
  userProfile: PropTypes.shape({
    fullName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired
  }),
  onClick: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  selectorPrimaryFocus: PropTypes.string
};

export { IlHeaderAction as default };
